import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import InfoComponent from "../../components/InfoComponent";
import Panels from "../../components/Panels";
import { useDispatch, useSelector } from "react-redux";
import { fetchPanelList } from "../../features/panels/panelsSlice";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/Loader";
import {
  fetchcomicTitle,
  selectComicSubTitle,
  selectComicTitle,
  selectComicTitleStatus,
} from "../../features/comicTitle/comicTitleSlice";
import { submitJobPost } from "../../api/submitJobPost";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadPost } from "../../api/downloadPost";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { getUserTranslationPermission } from "../../features/userAuths/userAuthsSlice";
import CharacterInfoModal from "../../components/CharacterInfoModal";
import {
  fetchCharacterData,
  addCharacter,
  selectCharacterData
} from "../../features/characterInfo/characterInfoSlice";

const ExtractionId = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isUpdated, setIsUpdated] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isUpdatedComicTitle, setIsUpdatedComicTitle] = useState(false);
  const navigate = useNavigate();
  const panelStatus = useSelector((state) => state.panels.status);
  const TranslationPer = useSelector(getUserTranslationPermission);
  const comicTitleStatus = useSelector(selectComicTitleStatus);
  const comicSubTitle = useSelector(selectComicSubTitle);
  const comicTitle = useSelector(selectComicTitle);

  // Character modal state
  const [characterInfoOpen, setCharacterInfoOpen] = useState(false);

  // Get character data from Redux
  const characterData = useSelector(selectCharacterData);

  useEffect(() => {
    if (panelStatus !== "loading" && (panelStatus === "idle" || !isUpdated)) {
      dispatch(fetchPanelList(id));
      setIsUpdated(true);
    }
  }, [panelStatus, dispatch, id, isUpdated]);

  useEffect(() => {
    if (
      comicTitleStatus !== "loading" &&
      (comicTitleStatus === "idle" || !isUpdatedComicTitle)
    ) {
      dispatch(fetchcomicTitle(id));
      setIsUpdatedComicTitle(true);
    }
  }, [comicTitleStatus, dispatch, id, isUpdatedComicTitle]);

  // Fetch character data when component mounts
  useEffect(() => {
    dispatch(fetchCharacterData(id));
  }, [dispatch, id]);

  const handleSubmit = async () => {
    await submitJobPost("ai-translation", id);
    navigate("/extraction");
  };

  const handleShowCharacterInfo = () => {
    // Make sure character data is loaded before showing the modal
    if (!characterData) {
      dispatch(fetchCharacterData(id));
    }
    setCharacterInfoOpen(true);
  };

  const handleAddCharacter = async (newCharacter) => {
    try {
      // Add the character and update the Redux store
      await dispatch(addCharacter({ comicId: id, character: newCharacter }));

      // Trigger panel update to refresh speaker dropdowns
      setIsUpdated(false);
    } catch (error) {
      console.error("Failed to add character:", error);
    }
  };

  const handleMakePanels = () => {
    navigate(`/PanelAnnotator/${id}`);
  };

  if (panelStatus !== "succeeded" && comicTitleStatus !== "succeeded") {
    return <Loader />;
  } else {
    return (
      <>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h6" mb="3px">
              Extraction
            </Typography>
            <Box sx={{ display: "flex" }}>
              <Typography variant="h4" fontWeight="bold">
                {comicTitle} / {comicSubTitle}
              </Typography>
              <InfoComponent />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              size="small"
              sx={{
                padding: "10px",
                borderRadius: "10px",
              }}
              onClick={handleShowCharacterInfo}
            >
              Character Info
            </Button>
            <Button
              variant="outlined"
              endIcon={<DownloadIcon />}
              size="small"
              sx={{
                padding: "10px",
                borderRadius: "10px",
              }}
              disabled={isDisabled}
              onClick={() => {
                setIsDisabled(true);
                downloadPost("extraction", id).then((res) => {
                  setIsDisabled(false);
                });
              }}
            >
              Download Results
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ padding: "10px", borderRadius: "10px" }}
              onClick={handleMakePanels}
            >
              Make Panels
            </Button>
            {TranslationPer && (
              <Button
                variant="outlined"
                endIcon={<SendIcon />}
                size="small"
                sx={{ padding: "10px", borderRadius: "10px" }}
                onClick={handleSubmit}
              >
                Submit for Translation
              </Button>
            )}
          </Box>
        </Box>
        <DndProvider backend={HTML5Backend}>
          <Panels />
        </DndProvider>

        {/* Character Info Modal - Using Redux state */}
        <CharacterInfoModal
          open={characterInfoOpen}
          onClose={() => setCharacterInfoOpen(false)}
          characterData={characterData}
          onAddCharacter={handleAddCharacter}
        />
      </>
    );
  }
};

export default ExtractionId;
